/**
 * <cm-bundle-picker>
 *
 * When the shopper toggles the checkbox on and clicks "Add to cart" on the
 * main product form, this element intercepts the form submission and adds
 * the configured BUNDLE variant to the cart INSTEAD of the main product.
 *
 * Why replace, not augment: the bundle product (e.g. "Part 1 + Part 2")
 * already contains the main product. Adding both would double-count Part 1
 * in the cart. Customers checking the "add Part 2 and save" option want the
 * single bundle line, not the standalone main product plus the bundle.
 *
 * Availability is intentionally not gated client-side. If the bundle variant
 * is set to "Continue selling when out of stock" (Shopify inventory policy),
 * /cart/add.js will accept it as a preorder. The actual discount, if any,
 * must be configured as a Shopify automatic discount on the bundle product.
 */

const SOURCE_TAG = 'cm-bundle-picker';

class CmBundlePicker extends HTMLElement {
  constructor() {
    super();
    this._inFlight = false;
    this._onFormSubmit = this._onFormSubmit.bind(this);
  }

  connectedCallback() {
    // Listen on `document` in CAPTURE phase. The companion <cm-product-form>
    // registers its own capture-phase listener on itself; capture descends
    // top-down, so document fires first and stopImmediatePropagation() here
    // prevents <cm-product-form> from adding the main product on top of our
    // bundle add.
    document.addEventListener('submit', this._onFormSubmit, { capture: true });
  }

  disconnectedCallback() {
    document.removeEventListener('submit', this._onFormSubmit, { capture: true });
  }

  _findProductFormForThisPicker() {
    const component =
      this.closest('product-form-component') ||
      this.closest('.shopify-section')?.querySelector('product-form-component') ||
      document.querySelector(`product-form-component[data-product-id="${this.mainProductId}"]`);
    return component?.querySelector('form') || null;
  }

  get isChecked() {
    const input = this.querySelector('.cm-bundle-picker__input');
    return Boolean(input && input.checked && !input.disabled);
  }

  get bundleVariantId() {
    return this.dataset.bundleVariantId;
  }

  get bundleProductHandle() {
    return this.dataset.bundleProductHandle;
  }

  get mainProductId() {
    return this.dataset.mainProductId;
  }

  /** @param {SubmitEvent} event */
  _onFormSubmit(event) {
    const form = /** @type {HTMLFormElement | null} */ (event.target);
    const ownForm = this._findProductFormForThisPicker();

    if (!this.isChecked) return;
    if (this._inFlight) return;

    if (!(form instanceof HTMLFormElement)) return;
    if (form.dataset.type && form.dataset.type !== 'add-to-cart-form') return;

    // Make sure this submit belongs to OUR picker's product form, not some
    // other product form on the page (e.g. quick-add).
    if (!ownForm || ownForm !== form) return;

    const variantId = this.bundleVariantId;
    if (!variantId) return;

    // Block the default form submission AND prevent <cm-product-form>'s
    // capture-phase handler (registered on cm-product-form itself, lower
    // in the capture path) from adding the main product on top of our
    // bundle add.
    event.preventDefault();
    event.stopImmediatePropagation();

    const quantity = this._readQuantity(form);
    const properties = this._readPreorderProperties(form);
    this._addBundle(variantId, quantity, form, properties);
  }

  /**
   * @param {HTMLFormElement | null | undefined} form
   * @returns {number}
   */
  _readQuantity(form) {
    if (!form) return 1;
    const input = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="quantity"]'));
    const value = Number(input?.value);
    if (Number.isFinite(value) && value > 0) return value;
    return 1;
  }

  /**
   * Mirror the main product's pre-order line-item property onto the bundle
   * line. The main form carries `<input data-cm-preorder-property
   * name="properties[Pre-order]">` which is disabled unless the selected
   * variant is a pre-order. When enabled, the bundle (which contains the
   * main product) must carry the same property so it isn't picked, packed
   * and shipped ahead of the pre-order item.
   * @param {HTMLFormElement | null | undefined} form
   * @returns {Record<string, string>}
   */
  _readPreorderProperties(form) {
    /** @type {Record<string, string>} */
    const properties = {};
    if (!form) return properties;
    const inputs = form.querySelectorAll('input[data-cm-preorder-property]');
    inputs.forEach((node) => {
      if (!(node instanceof HTMLInputElement)) return;
      if (node.disabled) return;
      const name = node.name || '';
      const match = name.match(/^properties\[(.+)\]$/);
      if (!match) return;
      properties[match[1]] = node.value;
    });
    return properties;
  }

  /**
   * @param {string} variantId
   * @param {number} quantity
   * @param {HTMLFormElement | null} form
   * @param {Record<string, string>} [properties]
   */
  async _addBundle(variantId, quantity, form, properties) {
    this._inFlight = true;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    cartItemsComponents.forEach((node) => {
      if (node instanceof HTMLElement && node.dataset.sectionId) {
        sectionIds.push(node.dataset.sectionId);
      }
    });

    /** @type {{ id: number, quantity: number, properties?: Record<string, string> }} */
    const item = {
      id: Number(variantId),
      quantity: Number(quantity) || 1,
    };
    if (properties && Object.keys(properties).length > 0) {
      item.properties = properties;
    }

    const payload = { items: [item] };
    if (sectionIds.length > 0) {
      payload.sections = sectionIds.join(',');
    }

    try {
      const cartAddUrl =
        (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.status) {
        console.warn('[cm-bundle-picker] Bundle add failed:', result.message || result);
        return;
      }

      // Refresh the cart so cart-drawer / header bubble update with the true
      // item count. We don't compute itemCount from `quantity` alone because
      // the bundle line might combine with an existing identical line.
      let cart = null;
      try {
        const cartResponse = await fetch('/cart.js');
        cart = await cartResponse.json();
      } catch (cartError) {
        console.warn('[cm-bundle-picker] Failed to refresh cart:', cartError);
      }

      const totalItemCount =
        cart && typeof cart.item_count === 'number' ? cart.item_count : Number(quantity) || 1;

      const updateEvent = new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart || undefined,
          sourceId: this.id || SOURCE_TAG,
          data: {
            source: SOURCE_TAG,
            itemCount: totalItemCount,
            variantId,
            productId: this.mainProductId,
            sections: result.sections,
          },
        },
      });
      (form || document).dispatchEvent(updateEvent);
    } catch (error) {
      console.error('[cm-bundle-picker] Unexpected error adding bundle:', error);
    } finally {
      this._inFlight = false;
    }
  }
}

if (!customElements.get('cm-bundle-picker')) {
  customElements.define('cm-bundle-picker', CmBundlePicker);
}
